import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import {
  ConnectorModel,
  ELMA365ConnectorSettingsOutput,
  ELMA365ToolkitToolsetSettings,
  ELMA365ToolsetModel,
} from '../../../../../../gamma-api';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { defaultApi } from '../../../../../apis';
import IbDivider from '../../../../../components/common/IbDivider';
import IbTypography from '../../../../../components/common/IbTypography';

import BaseToolsetListItem from './BaseToolsetListItem';
import { FOOTER_CLASS_NAME, SECTION_CLASS_NAME, SKELETON_HEIGHT, SKELETON_WIDTH } from './const';

import { IToolsetListItemProps } from './index';

const ELMA365ToolkitToolset: React.FC<IToolsetListItemProps> = ({
  toolset,
  onSetup,
  onDelete,
  onToolsetStateChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [connector, setConnector] = useState<ConnectorModel>();
  const [toolkit, setToolkit] = useState<ELMA365ToolsetModel>();

  const settings = toolset.settings as ELMA365ToolkitToolsetSettings;
  const translate = getTranslateFunc('Agent: Toolset: List: Item: ELMA365 Toolkit:');

  const fetchElma365ToolkitToolset = async () => {
    try {
      const response = await defaultApi.getOneELMA365ToolkitToolset(settings.toolkit_id);
      setToolkit(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchConnector = async () => {
    if (!settings.connector_id) return;
    try {
      const response = await defaultApi.getOneConnector(settings.connector_id);
      setConnector(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitialLoadAsync = async () => {
    setLoading(true);
    await fetchElma365ToolkitToolset();
    await fetchConnector();
    setLoading(false);
  };

  useEffect(() => void onInitialLoadAsync(), [settings.toolkit_id, settings.connector_id]);

  const getToolCount = () => {
    if (!toolkit) return;
    return toolkit.namespaces
      .map((namespace) => namespace.volume.tool_count ?? 0)
      .reduce((acc, value) => acc + value, 0);
  };

  const getConnectorInfo = (): string => {
    if (!connector) return '–';
    const elma365ConnectorSettings = connector.settings as ELMA365ConnectorSettingsOutput;
    const authType = translate(`Connector: ${elma365ConnectorSettings.apis.web.auth.type}`);
    return `${connector.name} – ${authType} – ${elma365ConnectorSettings.apis.web.url}`;
  };

  const getNamespacesTitle = (): string => {
    const title = translate('Namespaces');
    if (!toolkit) return title;
    return `${title} (${toolkit.namespaces.length})`;
  };

  const getNamespaceNames = (): string => {
    if (!toolkit) return '–';
    return toolkit.namespaces.map((namespace) => namespace.identity.name).join(', ');
  };

  const renderFooter = () => {
    return (
      <>
        <IbDivider />
        <div className={FOOTER_CLASS_NAME}>
          <div className={SECTION_CLASS_NAME}>
            <IbTypography.Paragraph disabled>{translate('Connector')}</IbTypography.Paragraph>
            {loading ? (
              <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
            ) : (
              <IbTypography.Paragraph>{getConnectorInfo()}</IbTypography.Paragraph>
            )}
          </div>
          <IbDivider type="vertical" />
          <div className={SECTION_CLASS_NAME}>
            <IbTypography.Paragraph disabled>{getNamespacesTitle()}</IbTypography.Paragraph>
            {loading ? (
              <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
            ) : (
              <IbTypography.Paragraph>{getNamespaceNames()}</IbTypography.Paragraph>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <BaseToolsetListItem
      footer={renderFooter()}
      loading={loading}
      name={toolkit?.name}
      toolCount={getToolCount()}
      toolset={toolset}
      onDelete={onDelete}
      onSetup={onSetup}
      onToolsetStateChange={onToolsetStateChange}
    />
  );
};

export default ELMA365ToolkitToolset;
